import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Heart, Trash2, Send, Loader2, Users, Newspaper, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { UserProfileCard } from "@/components/UserProfileCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const timeAgo = (iso, id) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return id ? "baru saja" : "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}j`;
  return `${Math.floor(s / 86400)}h`;
};

export default function Feed() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const id = lang === "id";
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [cardId, setCardId] = useState(null);
  const { data: feed, isLoading } = useQuery({ queryKey: ["feed"], queryFn: async () => (await api.get("/feed")).data });

  const post = async () => {
    const val = text.trim();
    if (!val || posting) return;
    setPosting(true);
    try {
      await api.post("/posts", { text: val });
      setText("");
      qc.invalidateQueries({ queryKey: ["feed"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setPosting(false); }
  };

  const like = async (p) => {
    qc.setQueryData(["feed"], (old) => (old || []).map((x) => x.id === p.id ? { ...x, liked: !x.liked, likes: x.likes + (x.liked ? -1 : 1) } : x));
    try { await api.post(`/posts/${p.id}/like`); } catch { qc.invalidateQueries({ queryKey: ["feed"] }); }
  };

  const del = async (p) => {
    qc.setQueryData(["feed"], (old) => (old || []).filter((x) => x.id !== p.id));
    try { await api.delete(`/posts/${p.id}`); } catch { qc.invalidateQueries({ queryKey: ["feed"] }); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <SectionHeader title={id ? "Beranda" : "Feed"} subtitle={id ? "Bagikan progresmu ke teman" : "Share your progress with friends"} />
        <Link to="/friends"><Button variant="outline" className="gap-2" data-testid="feed-friends-btn"><Users className="h-4 w-4" /> {id ? "Teman" : "Friends"}</Button></Link>
      </div>

      <div className="rounded-2xl border bg-card p-4" data-testid="post-composer">
        <div className="flex gap-3">
          <Avatar className="h-10 w-10 shrink-0"><AvatarImage src={user?.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-sm font-semibold text-primary">{initials(user?.name)}</AvatarFallback></Avatar>
          <div className="flex-1">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={id ? "Apa yang kamu pikirkan?" : "What's on your mind?"} data-testid="post-input" />
            <div className="mt-2 flex justify-end">
              <Button onClick={post} disabled={posting || !text.trim()} className="gap-2" data-testid="post-submit">
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {id ? "Posting" : "Post"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4" data-testid="feed-skeleton">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl border bg-card p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-32 animate-pulse rounded bg-muted" />
                  <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <div className="h-3 w-full animate-pulse rounded bg-muted" />
                <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
              </div>
              <div className="mt-4 h-3 w-24 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : !feed?.length ? (
        <EmptyState icon={Newspaper} title={id ? "Belum ada postingan" : "No posts yet"} description={id ? "Tambahkan teman dan mulai berbagi progres latihanmu!" : "Add friends and start sharing your progress!"} />
      ) : (
        <div className="space-y-4" data-testid="feed-list">
          {feed.map((p) => (
            <PostCard key={p.id} p={p} user={user} id={id} onLike={like} onDelete={del} qc={qc} onOpenProfile={setCardId} />
          ))}
        </div>
      )}

      <UserProfileCard userId={cardId} open={!!cardId} onClose={() => setCardId(null)} />
    </div>
  );
}

function PostCard({ p, user, id, onLike, onDelete, qc, onOpenProfile }) {
  const [showComments, setShowComments] = useState((p.comments?.length || 0) > 0);
  const [cText, setCText] = useState("");
  const [cBusy, setCBusy] = useState(false);
  const comments = p.comments || [];

  const addComment = async () => {
    const v = cText.trim();
    if (!v || cBusy) return;
    setCBusy(true);
    try {
      await api.post(`/posts/${p.id}/comments`, { text: v });
      setCText("");
      setShowComments(true);
      qc.invalidateQueries({ queryKey: ["feed"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setCBusy(false); }
  };

  const delComment = async (c) => {
    qc.setQueryData(["feed"], (old) => (old || []).map((x) => x.id === p.id
      ? { ...x, comments: (x.comments || []).filter((y) => y.id !== c.id), comment_count: Math.max(0, (x.comment_count || 1) - 1) } : x));
    try { await api.delete(`/posts/${p.id}/comments/${c.id}`); } catch { qc.invalidateQueries({ queryKey: ["feed"] }); }
  };

  return (
    <div className="rounded-2xl border bg-card p-4" data-testid={`post-${p.id}`}>
      <div className="flex items-center gap-3">
        <button onClick={() => onOpenProfile(p.user_id)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`post-author-${p.id}`}>
          <Avatar className="h-10 w-10"><AvatarImage src={p.author_picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-sm font-semibold text-primary">{initials(p.author_name)}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate font-display font-semibold transition-colors hover:text-primary">{p.author_name}</div>
            <div className="text-xs text-muted-foreground">{timeAgo(p.created_at, id)}</div>
          </div>
        </button>
        {p.mine && <button onClick={() => onDelete(p)} aria-label="Delete" className="-m-2 grid h-10 w-10 place-items-center rounded-lg text-muted-foreground hover:text-destructive" data-testid={`post-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      {p.text && <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed">{p.text}</p>}
      {p.image && <img src={p.image} alt="" className="mt-3 max-h-96 w-full rounded-xl object-cover" />}
      <div className="mt-3 flex items-center gap-4 border-t pt-3">
        <button onClick={() => onLike(p)} data-testid={`post-like-${p.id}`}
          className={cn("-my-2.5 flex items-center gap-1.5 py-2.5 text-sm font-medium transition-colors", p.liked ? "text-red-500" : "text-muted-foreground hover:text-red-500")}>
          <Heart className={cn("h-4 w-4", p.liked && "fill-current")} /> {p.likes > 0 ? p.likes : ""} {id ? "Suka" : "Like"}
        </button>
        <button onClick={() => setShowComments((v) => !v)} data-testid={`post-comment-toggle-${p.id}`}
          className="-my-2.5 flex items-center gap-1.5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
          <MessageCircle className="h-4 w-4" /> {p.comment_count > 0 ? p.comment_count : ""} {id ? "Komentar" : "Comment"}
        </button>
      </div>

      {showComments && (
        <div className="mt-3 space-y-3 border-t pt-3" data-testid={`comments-${p.id}`}>
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2.5" data-testid={`comment-${c.id}`}>
              <button onClick={() => onOpenProfile(c.user_id)} data-testid={`comment-author-${c.id}`}>
                <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={c.author_picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">{initials(c.author_name)}</AvatarFallback></Avatar>
              </button>
              <div className="min-w-0 flex-1 rounded-2xl bg-muted/50 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => onOpenProfile(c.user_id)} className="truncate text-sm font-semibold transition-colors hover:text-primary" data-testid={`comment-name-${c.id}`}>{c.author_name}</button>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{timeAgo(c.created_at, id)}</span>
                    {(c.mine || p.mine) && <button onClick={() => delComment(c)} aria-label="Delete" className="-m-2 grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:text-destructive" data-testid={`comment-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{c.text}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={user?.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">{initials(user?.name)}</AvatarFallback></Avatar>
            <input value={cText} onChange={(e) => setCText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
              placeholder={id ? "Tulis balasan..." : "Write a reply..."} maxLength={500}
              className="min-w-0 flex-1 rounded-full border bg-background px-4 py-2 text-sm outline-none focus:border-primary/50" data-testid={`comment-input-${p.id}`} />
            <Button size="icon" onClick={addComment} disabled={cBusy || !cText.trim()} className="h-9 w-9 shrink-0 rounded-full" data-testid={`comment-submit-${p.id}`}>
              {cBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
